import React, { useState, useEffect, useRef } from 'react';
import { completeMfaSignIn } from '../firebase';

const MfaVerification = ({ verificationId, resolver, onSuccess, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);
  const inputRef = useRef(null);

  // Check if resolver is valid
  useEffect(() => {
    if (!resolver || typeof resolver.resolveSignIn !== 'function') {
      console.error("Invalid resolver provided to MfaVerification:", resolver);
      setError("Authentication session is invalid. Please try signing in again.");
    }
  }, [resolver]);

  useEffect(() => {
    // We don't need to create the container here anymore
    // as that's handled by our enhanced functions in firebase.js
    
    // Focus the input field automatically on mobile
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
    
    return () => {
      // Make sure any lingering reCAPTCHA instances are cleared on unmount
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      if (!verificationCode.trim()) {
        throw new Error('Please enter the verification code');
      }

      // Validate resolver again before submitting
      if (!resolver || typeof resolver.resolveSignIn !== 'function') {
        throw new Error("Authentication session expired. Please try signing in again.");
      }

      const userCredential = await completeMfaSignIn(
        verificationId, 
        verificationCode, 
        resolver
      );
      
      onSuccess(userCredential);
    } catch (error) {
      console.error('MFA verification error:', error);
      // Provide a more user-friendly error message for reCAPTCHA issues
      if (error.message && error.message.includes('reCAPTCHA')) {
        setError('There was an issue with the security verification. Please try again.');
      } else if (error.message && error.message.includes('session expired')) {
        setError('Your authentication session has expired. Please try signing in again.');
      } else {
        setError(error.message || 'Failed to verify code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-black/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 dark:border-white/10 max-w-md mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Two-factor Authentication
      </h2>
      <p className="text-sm text-gray-700 dark:text-white/80 mb-4 sm:mb-6">
        Please enter the verification code sent to your phone.
      </p>

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="mb-4">
          <label 
            htmlFor="verification-code" 
            className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1"
          >
            Verification Code
          </label>
          <input
            ref={inputRef}
            id="verification-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="6"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 focus:outline-none focus:ring-2 focus:ring-brand dark:bg-black/50 text-gray-900 dark:text-white text-center text-lg tracking-widest"
            placeholder="000000"
            required
            disabled={loading}
            autoFocus
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 sm:mt-0 px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-3 rounded-lg bg-brand hover:bg-[#F9A06B] text-black font-medium transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        {/* This div will hold the invisible reCAPTCHA element */}
        <div id="recaptcha-container" className="mt-4"></div>
      </form>
    </div>
  );
};

export default MfaVerification; 